import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Paper,
  Stack,
  CircularProgress,
} from '@mui/material';
import { useQuery } from 'react-query';
import { horseApi, Horse } from '../../utils/api';
import { UnallocatedItem, MatchSuggestion } from '../../types/inbox';
import { inboxService } from '../../services/inboxService';

interface Props {
  open: boolean;
  item: UnallocatedItem | null;
  onClose: () => void;
  onAssign: (horseId: string) => void;
  /** Whether the parent's mutation is in flight (disables the Assign button). */
  saving?: boolean;
}

/**
 * Pick a horse to assign an unallocated result to. Two paths:
 *   - Click one of the suggested matches (rank-ordered by score)
 *   - Or type-ahead search any horse in the clinic
 *
 * The service-side scorer is reused here so the suggestions match exactly
 * what the user already saw on the inbox card.
 */
const AssignToPatientDialog: React.FC<Props> = ({ open, item, onClose, onAssign, saving }) => {
  const [search, setSearch] = useState('');
  const [selectedHorseId, setSelectedHorseId] = useState<string | null>(null);

  const { data: horses = [], isLoading } = useQuery<Horse[]>(
    ['horses-list-all'],
    () => horseApi.list(),
    { enabled: open, staleTime: 60_000 },
  );

  const suggestions: MatchSuggestion[] = useMemo(() => {
    if (!item) return [];
    return inboxService.scoreMatches(item, horses);
  }, [item, horses]);

  const filteredHorses = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return horses;
    return horses.filter(h => {
      const hay = [
        h.name,
        h.patientId,
        ...(Array.isArray(h.owners) ? (h.owners as Array<{ name?: string }>).map(o => o?.name ?? '') : []),
      ]
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [horses, search]);

  const handleClose = () => {
    setSearch('');
    setSelectedHorseId(null);
    onClose();
  };

  const handleAssign = () => {
    if (!selectedHorseId) return;
    onAssign(selectedHorseId);
  };

  if (!item) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Assign to patient</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {/* What we know about the result */}
          <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'action.hover' }}>
            <Typography variant="caption" color="text.secondary" display="block">
              {item.source} · {item.kind === 'imaging_study' ? item.modality ?? 'Imaging' : item.panelName ?? 'Lab'}
              {item.accessionNumber ? ` · ${item.accessionNumber}` : ''}
            </Typography>
            <Typography variant="body2">
              <strong>Patient on report:</strong> {item.patientNameOnReport ?? '—'}
              {item.patientIdOnReport ? ` (${item.patientIdOnReport})` : ''}
            </Typography>
            {item.ownerOnReport && (
              <Typography variant="body2">
                <strong>Owner:</strong> {item.ownerOnReport}
              </Typography>
            )}
          </Paper>

          {/* Suggested matches */}
          {suggestions.length > 0 && (
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Suggested matches
              </Typography>
              <Stack spacing={1} mt={1}>
                {suggestions.map(s => (
                  <Paper
                    key={s.horseId}
                    variant="outlined"
                    onClick={() => setSelectedHorseId(s.horseId)}
                    sx={{
                      p: 1.25,
                      cursor: 'pointer',
                      borderColor: selectedHorseId === s.horseId ? 'primary.main' : 'divider',
                      borderWidth: selectedHorseId === s.horseId ? 2 : 1,
                      '&:hover': { borderColor: 'primary.light' },
                    }}
                  >
                    <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
                      <Box>
                        <Typography variant="body2" fontWeight={500}>
                          {s.horseName}
                          {s.patientId ? ` · ${s.patientId}` : ''}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {s.ownerName ? `Owner: ${s.ownerName} · ` : ''}
                          {s.reasons.join(' · ')}
                        </Typography>
                      </Box>
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 700,
                          color: s.score >= 80 ? 'success.main' : s.score >= 50 ? 'warning.main' : 'text.secondary',
                        }}
                      >
                        {s.score}%
                      </Typography>
                    </Box>
                  </Paper>
                ))}
              </Stack>
            </Box>
          )}

          {/* Free search */}
          <Box>
            <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Search any patient
            </Typography>
            <TextField
              autoFocus={suggestions.length === 0}
              size="small"
              fullWidth
              placeholder="Type horse name, patient ID, or owner..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              sx={{ mt: 1 }}
            />
            {isLoading ? (
              <Box py={2} textAlign="center">
                <CircularProgress size={20} />
              </Box>
            ) : (
              <Stack spacing={0.5} mt={1} sx={{ maxHeight: 220, overflowY: 'auto' }}>
                {filteredHorses.slice(0, 30).map(h => (
                  <Paper
                    key={h.id}
                    variant="outlined"
                    onClick={() => setSelectedHorseId(h.id)}
                    sx={{
                      p: 1,
                      cursor: 'pointer',
                      borderColor: selectedHorseId === h.id ? 'primary.main' : 'divider',
                      borderWidth: selectedHorseId === h.id ? 2 : 1,
                      '&:hover': { borderColor: 'primary.light' },
                    }}
                  >
                    <Typography variant="body2">
                      {h.name}
                      {h.patientId ? (
                        <Typography component="span" variant="caption" color="text.secondary">
                          {' · '}
                          {h.patientId}
                        </Typography>
                      ) : null}
                    </Typography>
                  </Paper>
                ))}
                {filteredHorses.length > 30 && (
                  <Typography variant="caption" color="text.secondary" textAlign="center">
                    {filteredHorses.length - 30} more — refine search
                  </Typography>
                )}
              </Stack>
            )}
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleAssign}
          disabled={!selectedHorseId || saving}
          startIcon={saving ? <CircularProgress size={14} /> : undefined}
        >
          Assign
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssignToPatientDialog;
